import React, { useState, useEffect } from 'react';
import { getConfigComponent } from './StepConfigs';
import nodeLibrary from './nodeLibrary.json';
import { supabase } from '../../../../lib/supabaseClient';
import { useAuth } from '../../../../hooks/useAuth';

const FAVORITES_KEY = 'bonzai-node-favorites';

function loadFavorites() {
  try {
    const stored = localStorage.getItem(FAVORITES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function toSkillName(title) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function updateFrontmatter(template, field, value) {
  const match = template.match(/^---\n([\s\S]*?)\n---/);
  if (!match) return template;
  const fm = match[1];
  const regex = new RegExp(`^${field}:.*$`, 'm');
  if (regex.test(fm)) {
    const newFm = fm.replace(regex, `${field}: ${value}`);
    return template.replace(/^---\n[\s\S]*?\n---/, `---\n${newFm}\n---`);
  }
  const newFm = fm + `\n${field}: ${value}`;
  return template.replace(/^---\n[\s\S]*?\n---/, `---\n${newFm}\n---`);
}

export default function ConfigPanel({ step, onConfigChange, onDelete, canDelete, onClose, onOpenNodePanel }) {
  const ConfigComponent = getConfigComponent(step?.nodeId);
  const [favoriteIds, setFavoriteIds] = useState(loadFavorites);
  const [starHovered, setStarHovered] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [isUserFavorited, setIsUserFavorited] = useState(false);
  const { user, profile } = useAuth();

  useEffect(() => {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favoriteIds));
  }, [favoriteIds]);

  // Fetch favorites count and user's favorite status from Supabase
  useEffect(() => {
    if (!step?.nodeId || !supabase) return;

    const fetchFavorites = async () => {
      // Get total favorites count
      const { count } = await supabase
        .from('node_favorites')
        .select('*', { count: 'exact', head: true })
        .eq('node_id', step.nodeId);

      setFavoritesCount(count || 0);

      // Check if current user favorited this node
      if (user) {
        const { data } = await supabase
          .from('node_favorites')
          .select('id')
          .eq('node_id', step.nodeId)
          .eq('user_id', user.id)
          .single();

        setIsUserFavorited(!!data);
      }
    };

    fetchFavorites();
  }, [step?.nodeId, user]);

  const isFavorited = step?.nodeId ? favoriteIds.includes(step.nodeId) : false;
  const isTemplateStep = step && !['start', 'prompt', 'response'].includes(step.nodeId);
  const nodeData = nodeLibrary.nodes.find(n => n.id === step?.nodeId);
  const baseFavorites = nodeData?.favorites || 0;
  const displayFavorites = supabase ? favoritesCount : (isFavorited ? baseFavorites + 1 : baseFavorites);

  const toggleFavorite = async () => {
    if (!step?.nodeId) return;

    // Fallback to localStorage if no Supabase
    if (!supabase || !user) {
      setFavoriteIds(prev =>
        prev.includes(step.nodeId) ? prev.filter(id => id !== step.nodeId) : [...prev, step.nodeId]
      );
      return;
    }

    // Toggle in Supabase
    if (isUserFavorited) {
      // Unfavorite
      await supabase
        .from('node_favorites')
        .delete()
        .eq('node_id', step.nodeId)
        .eq('user_id', user.id);

      setIsUserFavorited(false);
      setFavoritesCount(prev => Math.max(0, prev - 1));
    } else {
      // Favorite
      await supabase
        .from('node_favorites')
        .insert({
          node_id: step.nodeId,
          user_id: user.id
        });

      setIsUserFavorited(true);
      setFavoritesCount(prev => prev + 1);
    }
  };

  const handlePublish = async () => {
    if (!supabase || !user || !step) return;

    setIsPublishing(true);
    setPublishSuccess(false);

    try {
      // Prepare node data
      const nodePayload = {
        node_id: step.nodeId || `custom-skill-${Date.now()}`,
        name: step.name || 'Untitled',
        type: step.config?.category || step.type || 'custom',  // Use category from dropdown
        icon: step.icon || '📋',
        description: step.config?.description || step.description || '',
        author_user_id: user.id,
        author_username: profile?.username || user.email?.split('@')[0] || 'anonymous',
        source_link: step.config?.source_link || null,
        parent_node_id: null,
        filepath: step.config?.filepath || `.claude/skills/${step.nodeId || 'untitled'}/SKILL.md`,
        is_published: true,
        template: step.config?.template || ''
      };

      // Insert or update node
      const { error } = await supabase
        .from('nodes')
        .upsert(nodePayload, { onConflict: 'node_id' });

      if (error) throw error;

      setPublishSuccess(true);
      setTimeout(() => setPublishSuccess(false), 3000);
    } catch (error) {
      console.error('Error publishing node:', error);
      alert('Failed to publish node: ' + error.message);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleTitleChange = (title) => {
    if (title.length > 50) return;
    const skillName = toSkillName(title);
    let newTemplate = (step.config?.template) || '';
    newTemplate = updateFrontmatter(newTemplate, 'name', skillName);
    onConfigChange({ ...step.config, title, skillName, template: newTemplate });
  };

  if (!step) {
    return null;
  }

  return (
    <div style={{
      width: '380px',
      borderLeft: '1px solid #e5e7eb',
      background: '#fff',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      flexShrink: 0,
      paddingTop: '56px'
    }}>
      {/* Header - matches NodePanel category header style */}
      <div style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ padding: '20px 24px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={() => { if (onOpenNodePanel) { onClose(); onOpenNodePanel(); } }}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#6b7280',
                  borderRadius: '4px',
                  transition: 'color 0.15s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#111827'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '18px', height: '18px' }}>
                  <path fillRule="evenodd" d="M17 10a.75.75 0 0 1-.75.75H5.612l4.158 3.96a.75.75 0 1 1-1.04 1.08l-5.5-5.25a.75.75 0 0 1 0-1.08l5.5-5.25a.75.75 0 1 1 1.04 1.08L5.612 9.25H16.25A.75.75 0 0 1 17 10Z" clipRule="evenodd" />
                </svg>
              </button>
              <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', margin: 0 }}>
                {step.name || 'Untitled'}
              </h2>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                padding: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                color: '#9ca3af',
                borderRadius: '4px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#6b7280'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '20px', height: '20px' }}>
                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Search bar - clickable, navigates back to NodePanel */}
        <div style={{ padding: '0 24px 16px' }}>
          <div
            onClick={() => { if (onOpenNodePanel) { onClose(); onOpenNodePanel(); } }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 14px',
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'border-color 0.15s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#8b5cf6'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#9ca3af" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
              <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clipRule="evenodd" />
            </svg>
            <span style={{ fontSize: '14px', color: '#9ca3af' }}>Search nodes...</span>
          </div>
        </div>
      </div>

      {/* Icon + favorites */}
      <div style={{ padding: '12px 24px 16px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '8px',
            background: step.iconBg || '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            flexShrink: 0
          }}>
            {step.icon}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {editingTitle && isTemplateStep ? (
              <div style={{ position: 'relative' }}>
                <input
                  autoFocus
                  value={step.name || ''}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  onBlur={() => setEditingTitle(false)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setEditingTitle(false); }}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#111827',
                    border: '1px solid #8b5cf6',
                    borderRadius: '6px',
                    padding: '4px 8px',
                    outline: 'none',
                    background: '#fff'
                  }}
                />
                <span style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '10px',
                  color: (step.name || '').length >= 50 ? '#ef4444' : '#9ca3af',
                  fontWeight: 500,
                  pointerEvents: 'none'
                }}>
                  {(step.name || '').length}/50
                </span>
              </div>
            ) : (
              <div
                onClick={isTemplateStep ? () => setEditingTitle(true) : undefined}
                style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  color: '#111827',
                  cursor: isTemplateStep ? 'text' : 'default',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={isTemplateStep ? 'Click to edit' : undefined}
              >
                {step.name || 'Untitled'}
              </div>
            )}
            {isTemplateStep && (
              <button
                onClick={toggleFavorite}
                onMouseEnter={() => setStarHovered(true)}
                onMouseLeave={() => setStarHovered(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0',
                  marginTop: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: (supabase ? isUserFavorited : isFavorited) ? '#facc15' : (starHovered ? '#d1d5db' : '#9ca3af'),
                  fontWeight: 500,
                  fontSize: '12px',
                  transition: 'color 0.15s ease'
                }}
              >
                {(supabase ? isUserFavorited : isFavorited) ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '13px', height: '13px' }}>
                    <path fillRule="evenodd" d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401Z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} style={{ width: '13px', height: '13px' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z" />
                  </svg>
                )}
                {displayFavorites} favorites
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Source link bar + author */}
      {isTemplateStep && (step.config?.source_link || step.config?.author_username) && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 24px',
          background: '#fafafa',
          borderBottom: '1px solid #f3f4f6',
          fontSize: '12px'
        }}>
          {step.config?.source_link ? (
            <a
              href={step.config.source_link}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#8b5cf6',
                cursor: 'pointer',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                textDecoration: 'none'
              }}
              title={step.config.source_link}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '13px', height: '13px' }}>
                <path d="M12.232 4.232a2.5 2.5 0 0 1 3.536 3.536l-1.225 1.224a.75.75 0 0 0 1.061 1.06l1.224-1.224a4 4 0 0 0-5.656-5.656l-3 3a4 4 0 0 0 .225 5.865.75.75 0 0 0 .977-1.138 2.5 2.5 0 0 1-.142-3.667l3-3Z" />
                <path d="M11.603 7.963a.75.75 0 0 0-.977 1.138 2.5 2.5 0 0 1 .142 3.667l-3 3a2.5 2.5 0 0 1-3.536-3.536l1.225-1.224a.75.75 0 0 0-1.061-1.06l-1.224 1.224a4 4 0 1 0 5.656 5.656l3-3a4 4 0 0 0-.225-5.865Z" />
              </svg>
              View source
            </a>
          ) : <span />}
          {step.config?.author_username && (
            <span style={{ color: '#9ca3af' }}>
              by <span style={{ fontWeight: 500, color: '#6b7280' }}>@{step.config.author_username}</span>
            </span>
          )}
        </div>
      )}

      {/* Publish Button - DISABLED FOR NOW */}
      {false && isTemplateStep && supabase && user && (
        <div style={{
          padding: '12px 24px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <button
            onClick={handlePublish}
            disabled={isPublishing}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              width: '100%',
              padding: '10px 16px',
              background: publishSuccess ? '#10b981' : '#3b82f6',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 600,
              color: '#fff',
              cursor: isPublishing ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s ease',
              opacity: isPublishing ? 0.6 : 1
            }}
            onMouseEnter={(e) => { if (!isPublishing && !publishSuccess) e.currentTarget.style.background = '#2563eb'; }}
            onMouseLeave={(e) => { if (!publishSuccess) e.currentTarget.style.background = '#3b82f6'; }}
          >
            {isPublishing ? (
              <>
                <svg style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Publishing...
              </>
            ) : publishSuccess ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                  <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd" />
                </svg>
                Published!
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px' }}>
                  <path d="M9.25 13.25a.75.75 0 0 0 1.5 0V4.636l2.955 3.129a.75.75 0 0 0 1.09-1.03l-4.25-4.5a.75.75 0 0 0-1.09 0l-4.25 4.5a.75.75 0 1 0 1.09 1.03L9.25 4.636v8.614Z" />
                  <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" />
                </svg>
                Publish to Community
              </>
            )}
          </button>
        </div>
      )}

      {/* Panel Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
        {ConfigComponent ? (
          <ConfigComponent
            config={step.config}
            onChange={onConfigChange}
            filepath={step.filepath}
          />
        ) : (
          <div style={{ color: '#6b7280', fontSize: '14px', textAlign: 'center', paddingTop: '40px' }}>
            No configuration options
          </div>
        )}
      </div>

      {/* Panel Footer */}
      {canDelete && (
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb'
        }}>
          <button
            onClick={onDelete}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              width: '100%',
              padding: '10px 16px',
              background: '#fff',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 500,
              color: '#dc2626',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.borderColor = '#fca5a5'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#fecaca'; }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px' }}>
              <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 0 0 6 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 1 0 .23 1.482l.149-.022.841 10.518A2.75 2.75 0 0 0 7.596 19h4.807a2.75 2.75 0 0 0 2.742-2.53l.841-10.519.149.023a.75.75 0 0 0 .23-1.482A41.03 41.03 0 0 0 14 4.193V3.75A2.75 2.75 0 0 0 11.25 1h-2.5ZM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4ZM8.58 7.72a.75.75 0 0 0-1.5.06l.3 7.5a.75.75 0 1 0 1.5-.06l-.3-7.5Zm4.34.06a.75.75 0 1 0-1.5-.06l-.3 7.5a.75.75 0 1 0 1.5.06l.3-7.5Z" clipRule="evenodd" />
            </svg>
            Remove Step
          </button>
        </div>
      )}
    </div>
  );
}
